import { CheckCircle, Clock3, FileQuestion, Loader2, Mail, Users } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { useCommitteeDashboardStats } from '@/hooks/use-committee';

interface CommitteeDashboardPageProps {
  eventId: string;
  onGoEvaluators: () => void;
  onGoPapers: () => void;
}

function valueOrPlaceholder(value: number | undefined): string {
  if (typeof value !== 'number') return '--';
  return String(value);
}

function percentForLoad(count: number, max: number): number {
  if (max <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((count / max) * 100)));
}

export function CommitteeDashboardPage({ eventId, onGoEvaluators, onGoPapers }: CommitteeDashboardPageProps) {
  const { data: stats, isLoading, isError } = useCommitteeDashboardStats(eventId);

  if (!eventId) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-gray-500">
          Seleccione un evento para visualizar el dashboard del comité.
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, idx) => (
            <Card key={`kpi-skeleton-${idx}`} className="shadow-sm">
              <CardContent className="space-y-3 pt-6">
                <Skeleton className="h-8 w-8 rounded-md" />
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-4 w-28" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card className="shadow-sm">
          <CardContent className="space-y-3 pt-6">
            <Skeleton className="h-6 w-56" />
            <Skeleton className="h-16 w-full" />
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="space-y-3 pt-6">
            <Skeleton className="h-6 w-56" />
            <Skeleton className="h-40 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <div className="mb-2 inline-flex items-center gap-2 text-red-700">
            <Loader2 className="h-4 w-4" />
            Error al cargar dashboard. Intente de nuevo.
          </div>
        </CardContent>
      </Card>
    );
  }

  const reviewTotal = stats?.enRevision?.total;
  const reviewRound1 = stats?.enRevision?.ronda1;
  const reviewRound2 = stats?.enRevision?.ronda2;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card className="shadow-sm">
          <CardContent className="pt-6">
            <Users className="h-8 w-8 rounded-md bg-teal-100 p-1.5 text-teal-700" />
            <p className="mt-4 text-4xl font-bold text-[#0f2744]">{valueOrPlaceholder(stats?.evaluadoresActivos)}</p>
            <p className="text-lg font-semibold text-gray-900">Evaluadores Activos</p>
            <p className="text-sm text-emerald-600">+2 esta semana</p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer shadow-sm hover:shadow-md" onClick={onGoEvaluators}>
          <CardContent className="pt-6">
            <Mail className="h-8 w-8 rounded-md bg-amber-100 p-1.5 text-amber-700" />
            <p className="mt-4 text-4xl font-bold text-[#0f2744]">{valueOrPlaceholder(stats?.invitacionesPendientes)}</p>
            <p className="text-lg font-semibold text-gray-900">Invitaciones Pendientes</p>
            <p className="text-sm text-gray-500">Esperando aceptación</p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer shadow-sm hover:shadow-md" onClick={onGoPapers}>
          <CardContent className="pt-6">
            <FileQuestion className="h-8 w-8 rounded-md bg-red-100 p-1.5 text-red-600" />
            <p className="mt-4 text-4xl font-bold text-[#0f2744]">{valueOrPlaceholder(stats?.sinAsignar)}</p>
            <p className="text-lg font-semibold text-gray-900">Sin Asignar</p>
            <p className="text-sm text-red-600">Requieren atención</p>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="pt-6">
            <Clock3 className="h-8 w-8 rounded-md bg-blue-100 p-1.5 text-blue-700" />
            <p className="mt-4 text-4xl font-bold text-[#0f2744]">{valueOrPlaceholder(reviewTotal)}</p>
            <p className="text-lg font-semibold text-gray-900">En Revisión</p>
            <p className="text-sm text-gray-500">
              Ronda 1: {valueOrPlaceholder(reviewRound1)} | Ronda 2: {valueOrPlaceholder(reviewRound2)}
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="pt-6">
            <CheckCircle className="h-8 w-8 rounded-md bg-green-100 p-1.5 text-green-700" />
            <p className="mt-4 text-4xl font-bold text-[#0f2744]">{valueOrPlaceholder(stats?.finalizadas)}</p>
            <p className="text-lg font-semibold text-gray-900">Finalizadas</p>
            <p className="text-sm text-green-600">--% completado</p>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-3xl text-[#1e3a5f]">Resumen de Actividad</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-16 w-full overflow-hidden rounded-md bg-gray-200">
            <div className="flex h-full">
              <div className="h-full bg-gray-400" style={{ width: '22%' }} />
              <div className="h-full bg-[#2f6fc5]" style={{ width: '30%' }} />
              <div className="h-full bg-[#c9a227]" style={{ width: '24%' }} />
              <div className="h-full bg-[#16a34a]" style={{ width: '24%' }} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-3xl text-[#1e3a5f]">Evaluadores - Carga de Trabajo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(stats?.evaluadorCarga || []).length === 0 ? (
            <div className="py-10 text-center text-gray-500">No hay ponencias registradas en este evento.</div>
          ) : (
            (stats?.evaluadorCarga || []).map((item) => {
              const percent = percentForLoad(item.count, item.max || 3);
              const barColor = percent >= 80 ? 'bg-orange-500' : 'bg-[#c9a227]';
              return (
                <div key={`${item.name}-${item.count}`} className="flex items-center gap-4">
                  <div className="w-56 truncate text-sm font-medium text-gray-900">{item.name}</div>
                  <div className="h-9 flex-1 overflow-hidden rounded-md bg-gray-200">
                    <div className={`h-full ${barColor} flex items-center justify-end pr-2 text-sm font-semibold text-white`} style={{ width: `${percent}%` }}>
                      {percent}%
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={onGoEvaluators} className="bg-[#c9a227] text-white hover:bg-[#b8921f]">
          + Invitar Evaluador
        </Button>
      </div>
    </div>
  );
}
